import { type ReactNode, useEffect, useId, useRef, useState } from 'react';

import { ArrowLeft, CaretDown, HandHeart, LockSimple } from '@phosphor-icons/react';
import classNames from 'classnames';
import { MotionConfig } from 'motion/react';

import { RichTextElement } from '../richTextElement';
import type { TipJarCopy, TipJarData } from './TipJar.types';
import { TipJarAmountPicker } from './TipJarAmountPicker';
import { TipJarCelebration } from './TipJarCelebration';
import { TipJarFeeCover } from './TipJarFeeCover';
import { TipJarMeter } from './TipJarMeter';
import { TipJarNote } from './TipJarNote';
import * as styles from './TipJarPage.css';
import { TipJarShare } from './TipJarShare';
import {
  formatTipAmount,
  getCurrencySymbol,
  getTipJarFeeCoverCents,
  getTipJarLayout,
  getTipJarProgress,
} from './tipJar.utils';
import { card, textButton, tokens } from './tipJarTheme.css';

export type TipJarStage = 'choose' | 'checkout' | 'thanks';

export type TipJarChipInOptions = {
  coverFee: boolean;
  message: string;
};

type TipJarPageProps = {
  jar: TipJarData;
  publicationName: string;
  publicationLogoUrl?: string | null;
  homeUrl?: string;
  shareUrl: string;
  copy: TipJarCopy;
  locale?: string;
  stage: TipJarStage;
  initialAmountCents?: number;
  checkout?: ReactNode;
  isStartingCheckout?: boolean;
  isLoadingCheckout?: boolean;
  errorMessage?: string | null;
  onChipIn: (amountCents: number, options: TipJarChipInOptions) => void;
  onChangeAmount: () => void;
  isAnimated?: boolean;
};

export const TipJarPage = ({
  jar,
  publicationName,
  publicationLogoUrl,
  homeUrl,
  shareUrl,
  copy,
  locale = 'en-US',
  stage,
  initialAmountCents,
  checkout,
  isStartingCheckout = false,
  isLoadingCheckout = false,
  errorMessage,
  onChipIn,
  onChangeAmount,
  isAnimated = true,
}: TipJarPageProps) => {
  const jarNameId = useId();
  const stageRef = useRef<HTMLDivElement>(null);
  const previousStage = useRef(stage);
  const [coverFee, setCoverFee] = useState(false);
  // Checkout still belongs to this tip, so the note survives it. Thanks starts the next one.
  const noteDraftKey = stage === 'thanks' ? 'thanks' : 'draft';

  // The control the reader used leaves with its stage, so focus follows them into the next one.
  useEffect(() => {
    if (previousStage.current === stage) return;

    previousStage.current = stage;
    stageRef.current?.focus();
  }, [stage]);

  const formatAmount = (amountCents: number) => formatTipAmount(amountCents, jar.currency, locale);
  const { hasGoal, percentFunded } = getTipJarProgress(jar.goal_cents, jar.raised_cents);
  const hasDescription = Boolean(jar.tiptap_state.content?.length);

  const layout = getTipJarLayout(jar.layout);

  const identity = (
    <>
      {publicationLogoUrl && <img className={styles.logo} src={publicationLogoUrl} alt="" />}
      {publicationName}
    </>
  );

  const intro = (
    <header className={classNames(styles.intro, layout === 'split' && styles.splitStory)}>
      {layout === 'split' && jar.banner_url && <img className={styles.splitBanner} src={jar.banner_url} alt="" />}

      {homeUrl ? (
        <a className={classNames(styles.identity, styles.identityLink)} href={homeUrl}>
          {identity}
        </a>
      ) : (
        <p className={styles.identity}>{identity}</p>
      )}

      {layout === 'centered' && jar.banner_url && <img className={styles.banner} src={jar.banner_url} alt="" />}

      <div className={styles.heading}>
        <h1 className={styles.title}>{jar.title}</h1>
        {hasDescription && (
          <TipJarDescription
            tiptapState={jar.tiptap_state}
            foldClassName={layout === 'split' ? styles.descriptionFoldedWhenStacked : styles.descriptionFolded}
            copy={copy}
          />
        )}
      </div>
    </header>
  );

  const jarSection = (
    <section
      className={classNames(styles.jar, layout === 'banner' ? styles.jarUnderStory : card)}
      aria-labelledby={jarNameId}
    >
      <h2 id={jarNameId} className={styles.jarName}>
        {copy.jarName}
      </h2>

      {hasGoal && jar.goal_cents !== null ? (
        <TipJarMeter
          goalCents={jar.goal_cents}
          raisedCents={jar.raised_cents ?? 0}
          supportersCount={jar.supporters_count}
          milestones={jar.milestones}
          formatAmount={formatAmount}
          copy={copy}
          isAnimated={isAnimated}
        />
      ) : (
        <p className={styles.supporters}>
          <span className={styles.supportersIcon} aria-hidden>
            <HandHeart size={20} />
          </span>
          {copy.supporters(jar.supporters_count)}
        </p>
      )}

      <div ref={stageRef} className={styles.stage} tabIndex={-1}>
        <TipJarNoteDraft key={noteDraftKey}>
          {(message, setMessage) => (
            <>
              {stage === 'choose' && (
                <TipJarAmountPicker
                  presetAmountsCents={jar.preset_amounts_cents}
                  minimumAmountCents={jar.minimum_amount_cents}
                  maximumAmountCents={jar.maximum_amount_cents}
                  initialAmountCents={initialAmountCents}
                  currencySymbol={getCurrencySymbol(jar.currency, locale)}
                  formatAmount={formatAmount}
                  copy={copy}
                  note={
                    <TipJarNoteDisclosure
                      message={message}
                      publicationName={publicationName}
                      maxLength={jar.message_max_length}
                      copy={copy}
                      onChange={setMessage}
                    />
                  }
                  isBusy={isStartingCheckout}
                  chargeCents={
                    jar.fee_cover_enabled && coverFee
                      ? (amountCents) => amountCents + getTipJarFeeCoverCents(amountCents, jar.fee_rate)
                      : undefined
                  }
                  onChipIn={(amountCents) =>
                    onChipIn(amountCents, { coverFee: jar.fee_cover_enabled && coverFee, message: message.trim() })
                  }
                >
                  {(amountCents) =>
                    jar.fee_cover_enabled ? (
                      <TipJarFeeCover
                        tipCents={amountCents}
                        feeRate={jar.fee_rate}
                        publicationName={publicationName}
                        formatAmount={formatAmount}
                        copy={copy}
                        isChecked={coverFee}
                        onChange={setCoverFee}
                      />
                    ) : null
                  }
                </TipJarAmountPicker>
              )}

              {stage === 'checkout' && (
                <div className={styles.checkout}>
                  <button type="button" className={textButton} onClick={onChangeAmount}>
                    <ArrowLeft size={14} weight="bold" aria-hidden />
                    {copy.changeAmount}
                  </button>
                  {isLoadingCheckout && (
                    <p className={styles.checkoutStatus} role="status">
                      {copy.checkoutLoading ?? 'Loading checkout'}
                    </p>
                  )}
                  {checkout ? <div className={styles.checkoutFrame}>{checkout}</div> : null}
                </div>
              )}

              {stage === 'thanks' && (
                <TipJarCelebration copy={copy} message={jar.thank_you_message} onChipInAgain={onChangeAmount} />
              )}
            </>
          )}
        </TipJarNoteDraft>
      </div>

      {errorMessage && (
        <p className={styles.error} role="alert">
          {errorMessage}
        </p>
      )}

      {stage !== 'thanks' && (
        <p className={styles.secureCheckout}>
          <LockSimple size={13} weight="bold" aria-hidden />
          {copy.secureCheckout}
        </p>
      )}
    </section>
  );

  const share = stage === 'thanks' && <TipJarShare url={shareUrl} text={copy.shareText(percentFunded)} copy={copy} />;

  return (
    <MotionConfig reducedMotion="user">
      <div className={styles.page} data-layout={layout}>
        {layout === 'banner' && (
          <>
            {jar.banner_url ? (
              <img className={styles.fullBleedBanner} src={jar.banner_url} alt="" />
            ) : (
              <div className={styles.headerBand} aria-hidden />
            )}
            <div className={styles.bannerColumn}>
              <div className={styles.bannerCard}>
                {intro}
                {jarSection}
              </div>
              {share}
            </div>
          </>
        )}

        {layout === 'split' && (
          <div className={styles.split}>
            {intro}
            {/* Stripe's checkout runs taller than a screen, so the jar only stays in view while the reader chooses. */}
            <SplitAside jar={jarSection} share={share} canStick={stage !== 'checkout'} />
          </div>
        )}

        {layout === 'centered' && (
          <div className={styles.column}>
            {intro}
            {jarSection}
            {share}
          </div>
        )}
      </div>
    </MotionConfig>
  );
};

type TipJarNoteDraftProps = {
  children: (message: string, setMessage: (message: string) => void) => ReactNode;
};

const TipJarNoteDraft = ({ children }: TipJarNoteDraftProps) => {
  const [message, setMessage] = useState('');
  return children(message, setMessage);
};

const TipJarNoteDisclosure = ({
  message,
  publicationName,
  maxLength,
  copy,
  onChange,
}: {
  message: string;
  publicationName: string;
  maxLength: number;
  copy: TipJarCopy;
  onChange: (value: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(message.length > 0);

  const removeNote = () => {
    onChange('');
    setIsOpen(false);
  };

  if (copy.addNote && !isOpen) {
    return (
      <button type="button" className={textButton} onClick={() => setIsOpen(true)}>
        {copy.addNote}
      </button>
    );
  }

  return (
    <TipJarNote
      value={message}
      maxLength={maxLength}
      publicationName={publicationName}
      copy={copy}
      onChange={onChange}
      onRemove={copy.addNote ? removeNote : undefined}
    />
  );
};

type SplitAsideProps = {
  jar: ReactNode;
  share: ReactNode;
  canStick: boolean;
};

const SplitAside = ({ jar, share, canStick }: SplitAsideProps) => {
  const asideRef = useRef<HTMLDivElement>(null);
  const jarRef = useRef<HTMLDivElement>(null);
  const shareRef = useRef<HTMLDivElement>(null);
  const [fitsScreen, setFitsScreen] = useState(false);

  useEffect(() => {
    const aside = asideRef.current;
    const jarElement = jarRef.current;
    const shareElement = shareRef.current;
    if (!canStick || !aside || !jarElement || !shareElement) return;

    const measure = () => {
      const gap = Number.parseFloat(getComputedStyle(aside).rowGap) || 0;
      const height = jarElement.offsetHeight + gap + shareElement.offsetHeight;
      setFitsScreen(height + styles.STICKY_MARGIN_PX * 2 <= window.innerHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    const observer = new ResizeObserver(measure);
    observer.observe(jarElement);
    observer.observe(shareElement);

    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [canStick]);

  const isStackSticky = canStick && fitsScreen;
  const isJarSticky = canStick && !fitsScreen;

  return (
    <div
      ref={asideRef}
      className={classNames(
        styles.splitAside,
        isStackSticky && styles.splitAsideSticky,
        isJarSticky && styles.splitAsideTrack,
      )}
    >
      <div className={classNames(isJarSticky && styles.splitJarTrack)}>
        <div ref={jarRef} className={classNames(isJarSticky && styles.splitJarSticky)}>
          {jar}
        </div>
      </div>
      <div ref={shareRef}>{share}</div>
    </div>
  );
};

type TipJarDescriptionProps = {
  tiptapState: TipJarData['tiptap_state'];
  foldClassName: string;
  copy: Pick<TipJarCopy, 'readMore' | 'showLess'>;
};

const TipJarDescription = ({ tiptapState, foldClassName, copy }: TipJarDescriptionProps) => {
  const frameId = useId();
  const frameRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isLong, setIsLong] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Measured while folded, since an opened description is as tall as its text. The fold can come and go with the page width, so the measure follows both sizes rather than running once.
  useEffect(() => {
    const frame = frameRef.current;
    const content = contentRef.current;
    if (isExpanded || !frame || !content) return;

    const measure = () => setIsLong(content.offsetHeight > frame.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    observer.observe(frame);

    return () => observer.disconnect();
  }, [isExpanded]);

  return (
    <>
      <div
        ref={frameRef}
        id={frameId}
        className={classNames(
          styles.description,
          !isExpanded && foldClassName,
          !isExpanded && isLong && styles.descriptionFaded,
        )}
      >
        <div ref={contentRef}>
          <RichTextElement
            element={{
              type: 'richText',
              attrs: {
                id: 'tip-jar-description',
                color: tokens.muted,
                linkColor: tokens.ink,
                fontSize: '17px',
                mobileFontSize: '16px',
                tiptapState,
              },
              content: [{ id: '', text: '' }],
            }}
          >
            {null}
          </RichTextElement>
        </div>
      </div>

      {(isLong || isExpanded) && (
        <button
          type="button"
          className={textButton}
          aria-expanded={isExpanded}
          aria-controls={frameId}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (copy.showLess ?? 'Show less') : (copy.readMore ?? 'Read more')}
          <CaretDown className={styles.readMoreIcon} size={12} weight="bold" aria-hidden />
        </button>
      )}
    </>
  );
};
